import { IconChevronsRight, IconSparkles } from "@tabler/icons-react"
import { type FC, Fragment, useEffect, useRef } from "react"

import { toNumberFormat } from "@forento/core/utilities/number"
import Tooltip from "@forento/ui/components/Tooltip"

import AiGenerationFeedback from "~/components/AiGenerationFeedback"
import Button from "~/components/Button"
import Link from "~/components/Link"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"

import type { WebsiteAiEdit } from "../useWebsiteAiEdit"
import EditProgress from "./EditProgress"
import ProposedChanges from "./ProposedChanges"

const quickPrompts = [
	{ label: "Sharpen the hero", message: "Make the hero section more conversational" },
	{ label: "Add social proof", message: "Add a section with social proof" },
	{ label: "Warmer colors", message: "Use a warmer color scheme" },
]

type Props = { ai: WebsiteAiEdit; isExpanded: boolean; onExpandedChange(isExpanded: boolean): void }

const AiWebsiteEditChat: FC<Props> = ({ ai, isExpanded, onExpandedChange }) => {
	const platform = usePlatform()!

	const messagesRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const element = messagesRef.current
		if (isExpanded && element !== null) element.scrollTop = element.scrollHeight
	}, [isExpanded, ai.turns, ai.sendingMessage])

	if (!isExpanded) {
		const undecidedChangeCount = ai.undecidedTurn?.changes?.length ?? null

		return (
			<aside className="flex w-14 shrink-0 flex-col items-center border-l border-black/6 bg-white py-3">
				<Tooltip tooltip="Edit with Forento AI">
					<button
						type="button"
						onClick={() => onExpandedChange(true)}
						aria-label="Expand Forento AI"
						className="border-primary bg-primary/10 text-primary hover:bg-primary/20 relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 px-2 py-3 transition-colors"
					>
						<IconSparkles className="size-5" />
						<span className="text-xs font-semibold [writing-mode:vertical-rl]">Forento AI</span>
						{undecidedChangeCount !== null && (
							<span className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-semibold text-white">
								{undecidedChangeCount > 9 ? "9+" : undecidedChangeCount}
							</span>
						)}
					</button>
				</Tooltip>
			</aside>
		)
	}

	return (
		<aside className="flex w-md shrink-0 flex-col border-l border-black/6 bg-white">
			<div className="flex items-center justify-between gap-2 border-b border-black/6 px-4 py-3">
				<div className="flex items-center gap-2">
					<IconSparkles className="text-primary size-5" />
					<h2 className="m-0 text-base font-semibold">Edit with Forento AI</h2>
				</div>
				<Tooltip tooltip="Collapse">
					<button
						type="button"
						onClick={() => onExpandedChange(false)}
						aria-label="Collapse Forento AI"
						className="cursor-pointer rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
					>
						<IconChevronsRight className="size-5" />
					</button>
				</Tooltip>
			</div>
			<div ref={messagesRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-gray-50 p-4">
				<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
					<p className="text-sm text-amber-900">
						<span className="font-semibold">Forento AI is in beta.</span> Nothing is changed until you apply
						the proposed edits, but review them before you do.
					</p>
				</div>
				<div className="max-w-[90%] self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900">
					Tell me what you'd like to change on your website. I can rewrite sections, add or remove them,
					update your page titles, and adjust the font and colors. Every change is previewed next to this chat
					before it is applied.
				</div>
				{ai.turns.map(turn => (
					<Fragment key={turn.id}>
						<div className="bg-primary max-w-[90%] self-end rounded-lg px-3 py-2 text-sm whitespace-pre-line text-white">
							{turn.message}
						</div>
						{turn.status === "completed" ? (
							<>
								<div className="max-w-[90%] self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm whitespace-pre-line text-gray-900">
									{turn.reply ?? "Done."}
								</div>
								{turn.changes !== null && turn.changes.length > 0 && (
									<ProposedChanges
										changes={turn.changes}
										decision={turn.decision}
										isApplying={ai.applyingTurnId === turn.id}
										isDiscarding={ai.discardingTurnId === turn.id}
										onApply={() => ai.apply(turn.id)}
										onDiscard={() => ai.discard(turn.id)}
									/>
								)}
								<AiGenerationFeedback id={turn.id} type="website-edit" />
							</>
						) : turn.status === "error" ? (
							<div className="max-w-[90%] self-start rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
								Something went wrong while preparing this edit. Please try again.
							</div>
						) : (
							<EditProgress progress={ai.progress} />
						)}
					</Fragment>
				))}
				{ai.sendingMessage !== null && (
					<>
						<div className="bg-primary max-w-[90%] self-end rounded-lg px-3 py-2 text-sm whitespace-pre-line text-white">
							{ai.sendingMessage}
						</div>
						<EditProgress progress={ai.progress} />
					</>
				)}
			</div>
			<div className="flex flex-col gap-3 border-t border-black/6 p-4">
				{ai.message.trim().length === 0 && (
					<div className="flex flex-wrap gap-2">
						{quickPrompts.map(prompt => (
							<button
								key={prompt.label}
								type="button"
								onClick={() => ai.setMessage(prompt.message)}
								className="hover:border-primary hover:text-primary cursor-pointer rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700"
							>
								{prompt.label}
							</button>
						))}
					</div>
				)}
				{!ai.hasEnoughCredits && (
					<p className="text-sm text-red-600">
						You need {toNumberFormat(ai.editCost)} AI credits to request an edit. Purchase more credits in
						Billing.
					</p>
				)}
				{ai.hasUnsavedChanges && (
					<p className="text-sm text-amber-700">
						Save your section edits before asking for a change, so Forento AI works from your latest
						website.
					</p>
				)}
				{ai.undecidedTurn !== null && (
					<p className="text-sm text-amber-700">
						Apply or discard the proposed changes above before sending another message.
					</p>
				)}
				<div className="flex items-end gap-2">
					<textarea
						value={ai.message}
						onChange={event => ai.setMessage(event.target.value)}
						onKeyDown={event => {
							if (event.key === "Enter" && !event.shiftKey) {
								event.preventDefault()
								ai.send()
							}
						}}
						placeholder='Describe a change, like "make the hero section more conversational"'
						rows={1}
						maxLength={2000}
						className="focus:border-primary field-sizing-content max-h-40 min-h-12 flex-1 resize-none rounded-lg border border-gray-300 bg-white p-3 text-sm focus:outline-none"
					/>
					<Button
						variant="primary"
						isDisabled={!ai.canSend || ai.message.trim().length === 0}
						isLoading={ai.isSending}
						onClick={ai.send}
					>
						Send
					</Button>
				</div>
				<div className="flex items-center justify-between gap-2 text-xs">
					<span className="text-gray-500">
						{toNumberFormat(ai.editCost)} credits per edit · Balance:{" "}
						{toNumberFormat(platform.aiCreditBalance)}
					</span>
					<Link to={routes.settings.billing.plans()} className="text-primary font-medium hover:underline">
						Purchase credits
					</Link>
				</div>
			</div>
		</aside>
	)
}

export default AiWebsiteEditChat
